// IndexedDB write-behind backup mirror for the plan.
//
// localStorage is the PRIMARY store (synchronous + simple), but Safari/iOS ITP
// can evict it after ~7 days of inactivity, and users occasionally clear "site
// data". IndexedDB is more durable (especially with navigator.storage.persist)
// and is evicted under different, less aggressive rules — so we mirror every
// successful save here and, on boot, restore from it when localStorage has been
// wiped. Best-effort: every op is guarded so a browser without IndexedDB (or a
// blocked one, e.g. some private modes) degrades to localStorage-only rather
// than throwing. This is a BACKUP, never the source of truth.
const DB_NAME = "pcp_backup"
const STORE   = "plan"
const KEY     = "current"

// PCPDesk desktop host: the plan is persisted PER CLIENT to its own file (via the
// storage bridge), and several client windows share ONE WebView2 profile — so this
// single-record IndexedDB mirror is both redundant (the file is durable) AND unsafe
// (it's shared across windows, so boot-recovery would restore the last-saved plan
// into a DIFFERENT client whose file is empty — cross-client contamination). Skip
// the mirror entirely in the desktop host; the per-client file is the source of truth.
const _desktop = () => typeof window !== "undefined" && !!window.__PCP_DESKTOP__

function _openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexedDB")); return }
    let req
    try { req = indexedDB.open(DB_NAME, 1) } catch (e) { reject(e); return }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror   = () => reject(req.error || new Error("idb open failed"))
  })
}

// Mirror the plan JSON string to IndexedDB. Fire-and-forget; never throws.
export function mirrorToIdb(json) {
  if (!json || _desktop()) return
  _openDb().then(db => {
    try {
      const tx = db.transaction(STORE, "readwrite")
      tx.objectStore(STORE).put({ json: json, ts: Date.now() }, KEY)
      tx.oncomplete = () => { try { db.close() } catch (_) {} }
      tx.onerror    = () => { try { db.close() } catch (_) {} }
    } catch (_) { try { db.close() } catch (_) {} }
  }).catch(() => { /* no IDB / blocked — localStorage remains the store */ })
}

// Read the backed-up plan JSON string (or null). Never throws/rejects.
export function readIdbBackup() {
  if (_desktop()) return Promise.resolve(null) // never cross-restore between client windows
  return _openDb().then(db => new Promise(resolve => {
    try {
      const tx  = db.transaction(STORE, "readonly")
      const req = tx.objectStore(STORE).get(KEY)
      req.onsuccess = () => { const v = req.result; try { db.close() } catch (_) {} ; resolve(v && v.json ? v.json : null) }
      req.onerror   = () => { try { db.close() } catch (_) {} ; resolve(null) }
    } catch (_) { try { db.close() } catch (_) {} ; resolve(null) }
  })).catch(() => null)
}

// Clear the IndexedDB backup (awaitable). MUST be called when the user starts a
// NEW/blank plan — otherwise restoreFromIdbIfNeeded() would repopulate the
// just-cleared plan from this mirror on the next load ("New PCP didn't clear").
// Never throws/rejects.
export function clearIdbBackup() {
  return _openDb().then(db => new Promise(resolve => {
    try {
      const tx = db.transaction(STORE, "readwrite")
      tx.objectStore(STORE).delete(KEY)
      tx.oncomplete = () => { try { db.close() } catch (_) {} ; resolve(true) }
      tx.onerror    = () => { try { db.close() } catch (_) {} ; resolve(false) }
    } catch (_) { try { db.close() } catch (_) {} ; resolve(false) }
  })).catch(() => false)
}

// Best-effort: ask the browser to make storage persistent so it resists the
// eviction that would otherwise wipe an inactive plan. No-op where unsupported.
export function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist && navigator.storage.persisted) {
      navigator.storage.persisted().then(p => { if (!p) navigator.storage.persist().catch(() => {}) }).catch(() => {})
    }
  } catch (_) { /* unsupported — nothing to do */ }
}
